/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { Button, CircularProgress, Grid, TextField } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";
import Modal from "../../Components/Modal";
import { Vocabulary } from "../../Utils/Vocabulary";
import { regexIBAN, regexSeriesAndNumber } from "../../Utils/Constants";
import theme from "../../Theme/Theme";

type EditClientModalProps = {
  open: boolean;
  customer: any | null;
  onClose: () => void;
  onSave: (customer: any) => Promise<void>;
};

const emptyCustomer = {
  name: "",
  email: "",
  personalData: {
    phoneNumber: "",
    iban: "",
    accountFullName: "",
    seriesAndNumber: "",
    note: "",
  },
};

export default function EditClientModal(props: EditClientModalProps) {
  const { open, customer, onClose, onSave } = props;
  const [localCustomer, setLocalCustomer] = useState<any>(emptyCustomer);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const notifyError = (msg: string) => toast.error(msg);
  const notifySuccess = (msg: string) => toast.success(msg);

  useEffect(() => {
    if (customer) {
      setLocalCustomer({
        ...emptyCustomer,
        ...customer,
        personalData: {
          ...emptyCustomer.personalData,
          ...(customer.personalData || {}),
        },
      });
    } else {
      setLocalCustomer(emptyCustomer);
    }
    setErrors({});
  }, [customer]);

  function validateField(value: string, name: string) {
    let error = "";
    if (name === "iban" && value) {
      if (!new RegExp(regexIBAN).test(value)) error = Vocabulary.ibanMessageError;
    }
    if (name === "seriesAndNumber" && value) {
      if (!new RegExp(regexSeriesAndNumber).test(value)) error = Vocabulary.seriesAndNumberMessageError;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  }

  function handlePersonalDataChange(field: string, value: string) {
    setLocalCustomer((prev: any) => ({
      ...prev,
      personalData: { ...prev.personalData, [field]: value },
    }));
    validateField(value, field);
  }

  function hasErrors() {
    return Object.values(errors).some((e) => !!e);
  }

  async function handleSave() {
    if (hasErrors()) return;
    setLoading(true);
    try {
      await onSave(localCustomer);
      notifySuccess(Vocabulary.saveSuccess);
      onClose();
    } catch {
      notifyError(Vocabulary.saveError);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Modal open={open} onClose={onClose} title={Vocabulary.editClient} maxWidth={700}>
        <Grid container spacing={2} style={{ padding: 16 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label={Vocabulary.name}
              variant="standard"
              fullWidth
              value={localCustomer.name}
              onChange={(e) => setLocalCustomer((prev: any) => ({ ...prev, name: e.target.value }))}
              style={{ margin: "8px 0" }}
            />
            <TextField
              label={Vocabulary.email}
              variant="standard"
              fullWidth
              value={localCustomer.email}
              onChange={(e) => setLocalCustomer((prev: any) => ({ ...prev, email: e.target.value }))}
              style={{ margin: "8px 0" }}
            />
            <TextField
              label={Vocabulary.phone}
              variant="standard"
              fullWidth
              value={localCustomer.personalData.phoneNumber}
              onChange={(e) => handlePersonalDataChange("phoneNumber", e.target.value)}
              style={{ margin: "8px 0" }}
            />
            <TextField
              label={Vocabulary.note}
              variant="standard"
              fullWidth
              multiline
              value={localCustomer.personalData.note}
              onChange={(e) => handlePersonalDataChange("note", e.target.value)}
              style={{ margin: "8px 0" }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={Vocabulary.accountFullName}
              variant="standard"
              fullWidth
              value={localCustomer.personalData.accountFullName}
              onChange={(e) => handlePersonalDataChange("accountFullName", e.target.value)}
              style={{ margin: "8px 0" }}
            />
            <TextField
              label={Vocabulary.iban}
              variant="standard"
              fullWidth
              value={localCustomer.personalData.iban}
              onChange={(e) => {
                const val = e.target.value.toUpperCase().replace(/\s/g, "");
                handlePersonalDataChange("iban", val);
              }}
              error={!!errors["iban"]}
              helperText={errors["iban"]}
              style={{ margin: "8px 0" }}
            />
            <TextField
              label={Vocabulary.seriesAndNumber}
              variant="standard"
              fullWidth
              value={localCustomer.personalData.seriesAndNumber}
              onChange={(e) => {
                const val = e.target.value.toUpperCase().replace(/\s/g, "");
                handlePersonalDataChange("seriesAndNumber", val);
              }}
              error={!!errors["seriesAndNumber"]}
              helperText={errors["seriesAndNumber"]}
              style={{ margin: "8px 0" }}
            />
          </Grid>
        </Grid>
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Button
            variant="contained"
            sx={{ color: theme.palette.whiteColor?.main }}
            color="primary"
            onClick={handleSave}
            disabled={hasErrors() || loading}
          >
            <SaveIcon /> &nbsp;&nbsp;{Vocabulary.save}
          </Button>
        </div>
      </Modal>
      {loading && (
        <div className="loading">
          <CircularProgress color="primary" />
        </div>
      )}
    </>
  );
}
